import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Select } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { LeadFilter } from '@shared/schemas.js';

/**
 * Admin assignment controls for the leads list.
 *
 * Compact on purpose — the whole feature is "give these leads to that VA".
 * There is no workload dashboard, no round-robin and no drag-and-drop, because
 * one owner assigning work to one assistant does not need a distribution engine.
 *
 * "Assign all matching" sends the FILTER, not the visible ids: the server
 * resolves it, so what gets assigned is what actually matches rather than
 * whatever happened to be on screen when the button was pressed.
 */
export function AssignmentBar({
  selectedIds,
  filter,
  matchingCount,
  allMatchingSelected = false,
  disabled = false,
  onDone,
}: {
  selectedIds: number[];
  filter: LeadFilter;
  matchingCount: number;
  allMatchingSelected?: boolean;
  disabled?: boolean;
  onDone: () => void;
}): JSX.Element | null {
  const { user, authEnabled } = useAuth();
  const queryClient = useQueryClient();
  const [userId, setUserId] = React.useState<string>('');
  const [busy, setBusy] = React.useState(false);

  // Only an admin can assign, and only when there are accounts to assign to.
  const isAdmin = !authEnabled || user?.role === 'admin';
  const users = useQuery({ queryKey: ['users'], queryFn: api.listUsers, enabled: isAdmin });

  // Active VAs only. The server rejects assigning a lead to an admin, so
  // offering one here would just produce a 422.
  const assignable = (users.data?.users ?? []).filter((u) => u.isActive && u.role === 'va');
  if (!isAdmin) return null;

  const selectionCount = allMatchingSelected ? matchingCount : selectedIds.length;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['leads'] });
    await queryClient.invalidateQueries({ queryKey: ['users'] });
    await queryClient.invalidateQueries({ queryKey: ['assignments'] });
    onDone();
  };

  const run = async (action: () => Promise<{ assigned?: number; unassigned?: number }>, verb: string) => {
    if (!userId && verb !== 'Unassigned') return;
    setBusy(true);
    try {
      const result = await action();
      toast.success(`${verb} ${result.assigned ?? result.unassigned ?? 0} lead(s)`);
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Assignment failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select aria-label="Assign to caller" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={busy || disabled} className="h-7 w-40 text-xs">
        <option value="">Assign to…</option>
        {assignable.map((u) => (
          <option key={u.id} value={u.id}>
            {u.displayName} ({u.activeAssignments})
          </option>
        ))}
      </Select>

      <Button
        size="sm"
        variant="ghost"
        disabled={busy || disabled || !userId || selectionCount === 0}
        onClick={() => {
          if (allMatchingSelected && !window.confirm(`Assign all ${matchingCount} leads matching the current filter?`)) return;
          void run(() => allMatchingSelected ? api.assignByFilter(filter, Number(userId)) : api.assignLeads(selectedIds, Number(userId)), 'Assigned');
        }}
      >
        Assign {selectionCount || ''} selected
      </Button>

      <Button
        size="sm"
        variant="ghost"
        disabled={busy || disabled || !userId || matchingCount === 0}
        onClick={() => {
          if (!window.confirm(`Assign all ${matchingCount} leads matching the current filter?`)) return;
          void run(() => api.assignByFilter(filter, Number(userId)), 'Assigned');
        }}
      >
        Assign all matching
      </Button>

      <Button
        size="sm"
        variant="ghost"
        disabled={busy || disabled || selectionCount === 0}
        onClick={() => {
          if (!window.confirm(`Unassign ${allMatchingSelected ? 'all ' : ''}${selectionCount} ${allMatchingSelected ? 'leads matching the current filter' : 'selected leads'}? Assignment history will be kept.`)) return;
          void run(() => allMatchingSelected ? api.unassignByFilter(filter) : api.unassignLeads(selectedIds), 'Unassigned');
        }}
      >
        Unassign selected
      </Button>
    </div>
  );
}
